import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import ResponsiveLayout from "@/mobile/ResponsiveLayout";
import StagingBanner from "@/components/staging/StagingBanner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Items from "@/pages/Items";
import Routing from "@/pages/Routing";
import MRP from "@/pages/MRP";
import WorkOrders from "@/pages/WorkOrders";
import Quality from "@/pages/Quality";
import ProductionFlow from "@/pages/ProductionFlow";
import ProductionFlowInventory from "@/pages/ProductionFlowInventory";
import ProductionApprovals from "@/pages/ProductionApprovals";
import RaiseTransferOrder from "@/pages/RaiseTransferOrder";
import UserPermissions from "@/pages/UserPermissions";
import Quotations from "@/pages/Quotations";
import RaiseQuotation from "@/pages/RaiseQuotation";
import StockLedger from "@/pages/StockLedger";
import StockAvailability from "@/pages/StockAvailability";
import QualityInspections from "@/pages/QualityInspections";
import Suppliers from "@/pages/Suppliers";
import PurchaseRequests from "@/pages/PurchaseRequests";
import PurchaseOrders from "@/pages/PurchaseOrders";
import AutoProcurement from "@/pages/AutoProcurement";
import Customers from "@/pages/Customers";
import SalesOrders from "@/pages/SalesOrders";
import OrderStatus from "@/pages/OrderStatus";
import OrderStatusSheet from "@/pages/OrderStatusSheet";
import CustomerSalesOrders from "@/pages/CustomerSalesOrders";
import ProformaInvoices from "@/pages/ProformaInvoices";
import PackingSlips from "@/pages/PackingSlips";
import DeliveryChallans from "@/pages/DeliveryChallans";
import TaxInvoices from "@/pages/TaxInvoices";
import Capacity from "@/pages/Capacity";
import GoodsInward from "@/pages/GoodsInward";
import GoodsReceipt from "@/pages/GoodsReceipt";
import Users from "@/pages/Users";
import Crm from "@/pages/Crm";
import IndiaMartLeads from "@/pages/IndiaMartLeads";
import SettingsPage from "@/pages/Settings";
import TallyData from "@/pages/TallyData";
import SalesVisits from "@/pages/SalesVisits";
import SalesExpenses from "@/pages/SalesExpenses";
import SalesPlans from "@/pages/SalesPlans";
import SalesTasks from "@/pages/SalesTasks";
import SalesReports from "@/pages/SalesReports";
import SalesHome from "@/pages/SalesHome";
import SalesReporterApp from "@/pages/SalesReporterApp";
import SalesProfile from "@/pages/SalesProfile";
import DailyDepartmentReports from "@/pages/DailyDepartmentReports";
import OtdCommandBoard from "@/pages/OtdCommandBoard";
import HrModule from "@/pages/HrModule";
import Logbook from "@/pages/hr/Logbook";
import IncomingInspectionNew from "@/pages/IncomingInspectionNew";
import IncomingInspectionHistory from "@/pages/IncomingInspectionHistory";
import IncomingInspectionNcr from "@/pages/IncomingInspectionNcr";
import IncomingInspectionNcrRegister from "@/pages/IncomingInspectionNcrRegister";
import Calibration from "@/pages/Calibration";
import ProductionNcrRegister from "@/pages/ProductionNcrRegister";
import ProductionReworkOrders from "@/pages/ProductionReworkOrders";
import Indents from "@/pages/Indents";
import DcVsGinReport from "@/pages/DcVsGinReport";
import PendingQcReport from "@/pages/PendingQcReport";
import PendingSalesOrderReport from "@/pages/PendingSalesOrderReport";
import PendingProductionReport from "@/pages/PendingProductionReport";
import PartnerList from "@/pages/partners/PartnerList";
import PartnerProfile from "@/pages/partners/PartnerProfile";
import PartnerDashboard from "@/pages/partners/PartnerDashboard";
import { PartnerApprovals, PartnerAlerts, PartnerReports } from "@/pages/partners/PartnerWorkspace";
import { SupplierRfqs, ProcurementPlan } from "@/pages/partners/SupplierProcurement";
import { DealerPriceLists, PartnerSettings } from "@/pages/partners/PartnerAdmin";
import DesignDashboard from "@/pages/design/DesignDashboard";
import DesignApprovals from "@/pages/design/DesignApprovals";
import DesignRequests from "@/pages/design/DesignRequests";
import DesignItems from "@/pages/design/DesignItems";
import DesignDocuments from "@/pages/design/DesignDocuments";
import DesignBoms from "@/pages/design/DesignBoms";
import DesignRoutings from "@/pages/design/DesignRoutings";
import DesignChanges from "@/pages/design/DesignChanges";
import DesignNpd from "@/pages/design/DesignNpd";
import DesignReports from "@/pages/design/DesignReports";
import DesignSettings from "@/pages/design/DesignSettings";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <StagingBanner />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ResponsiveLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="items" element={<Items />} />
              <Route path="routing" element={<Routing />} />
              <Route path="mrp" element={<MRP />} />
              <Route path="work-orders" element={<WorkOrders />} />
              <Route path="quality" element={<Quality />} />
              {/* Inventory is now part of Production Flow (one place for
                  stock across every store, not a separate top-level
                  module) — this keeps old bookmarks/links working. */}
              <Route path="inventory" element={<Navigate to="/production-flow/inventory" replace />} />
              <Route path="stock-ledger" element={<StockLedger />} />
              <Route path="stock-availability" element={<StockAvailability />} />
              <Route path="production-flow" element={<ProductionFlow />} />
              <Route path="production-flow/inventory" element={<ProductionFlowInventory />} />
              <Route path="production-flow/:stage" element={<ProductionFlow />} />
              <Route path="production-approvals" element={<ProductionApprovals />} />
              <Route path="raise-transfer-order" element={<RaiseTransferOrder />} />
              <Route path="user-permissions" element={<UserPermissions />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="raise-quotation" element={<RaiseQuotation />} />
              <Route path="raise-quotation/:id" element={<RaiseQuotation />} />
              <Route path="quality-inspections" element={<QualityInspections />} />
              {/* Incoming/In-Process Inspection — merged in from the standalone
                  SPC/NCR tool; lives as a dropdown under Quality in the sidebar. */}
              <Route path="quality/incoming-inspection" element={<IncomingInspectionNew />} />
              <Route path="quality/incoming-inspection/history" element={<IncomingInspectionHistory />} />
              <Route path="quality/incoming-inspection/history/:id" element={<IncomingInspectionHistory />} />
              <Route path="quality/incoming-inspection/ncr" element={<IncomingInspectionNcr />} />
              <Route path="quality/incoming-inspection/ncr-register" element={<IncomingInspectionNcrRegister />} />
              {/* Calibration — Master List of Instrument & Gauges, lives under
                  Quality in the sidebar next to Incoming/In-Process Inspection. */}
              <Route path="quality/calibration" element={<Calibration />} />
              {/* Production NCRs — auto-raised from Testing-stage out-of-spec
                  entries (see Quality Inspections' checkpoint entry). */}
              <Route path="quality/production-ncrs" element={<ProductionNcrRegister />} />
              {/* Rework Orders — auto-raised alongside a Production NCR on a
                  Testing/Final Inspection QC failure, tracks the working-day
                  SLA to rework + re-test by. */}
              <Route path="quality/rework-orders" element={<ProductionReworkOrders />} />
              {/* Indents — auto-raised material requisitions, Semi Finished → Assembly. */}
              <Route path="indents" element={<Indents />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="purchase-requests" element={<PurchaseRequests />} />
              <Route path="purchase-orders" element={<PurchaseOrders />} />
              <Route path="auto-procurement" element={<AutoProcurement />} />
              <Route path="customers" element={<Customers />} />
              {/* Supplier / Vendor Management + Dealer / Customer Management
                  (backend/partners/). Same pages, parameterised by kind. */}
              <Route path="supplier-master" element={<PartnerList kind="supplier" />} />
              <Route path="supplier-master/dashboard" element={<PartnerDashboard kind="supplier" />} />
              <Route path="supplier-master/approvals" element={<PartnerApprovals kind="supplier" />} />
              <Route path="supplier-master/alerts" element={<PartnerAlerts kind="supplier" />} />
              <Route path="supplier-master/reports" element={<PartnerReports kind="supplier" />} />
              <Route path="supplier-master/rfqs" element={<SupplierRfqs />} />
              <Route path="supplier-master/procurement-plan" element={<ProcurementPlan />} />
              <Route path="supplier-master/settings" element={<PartnerSettings kind="supplier" />} />
              <Route path="supplier-master/:id" element={<PartnerProfile kind="supplier" />} />
              <Route path="dealer-master" element={<PartnerList kind="dealer" />} />
              <Route path="dealer-master/dashboard" element={<PartnerDashboard kind="dealer" />} />
              <Route path="dealer-master/approvals" element={<PartnerApprovals kind="dealer" />} />
              <Route path="dealer-master/alerts" element={<PartnerAlerts kind="dealer" />} />
              <Route path="dealer-master/reports" element={<PartnerReports kind="dealer" />} />
              <Route path="dealer-master/pricing" element={<DealerPriceLists />} />
              <Route path="dealer-master/settings" element={<PartnerSettings kind="dealer" />} />
              <Route path="dealer-master/:id" element={<PartnerProfile kind="dealer" />} />
              <Route path="sales-orders" element={<SalesOrders />} />
              <Route path="order-status" element={<OrderStatus />} />
              <Route path="order-status-sheet" element={<OrderStatusSheet />} />
              <Route path="customer-sales-orders" element={<CustomerSalesOrders />} />
              <Route path="proforma-invoices" element={<ProformaInvoices />} />
              <Route path="packing-slips" element={<PackingSlips />} />
              <Route path="delivery-challans" element={<DeliveryChallans />} />
              <Route path="tax-invoices" element={<TaxInvoices />} />
              <Route path="reports" element={<Navigate to="/reports/dc-vs-gin" replace />} />
              <Route path="reports/dc-vs-gin" element={<DcVsGinReport />} />
              <Route path="reports/pending-qc" element={<PendingQcReport />} />
              <Route path="reports/pending-sales-orders" element={<PendingSalesOrderReport />} />
              <Route path="reports/pending-production" element={<PendingProductionReport />} />
              <Route path="capacity" element={<Capacity />} />
              <Route path="goods-inward" element={<GoodsInward />} />
              <Route path="goods-receipt" element={<GoodsReceipt />} />
              <Route path="users" element={<Users />} />
              <Route path="crm" element={<Crm />} />
              <Route path="crm/indiamart" element={<IndiaMartLeads />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="tally" element={<TallyData />} />
              {/* Daily Department Reports — new, additive route. Self-generated
                  from live data; every role sees its own department(s), admin
                  sees the combined view. */}
              <Route path="daily-reports" element={<DailyDepartmentReports />} />
              {/* OTD Command Board — daily production meeting board, open to
                  every role (backend/otd_board.py decides what each may change). */}
              <Route path="otd-board" element={<OtdCommandBoard />} />
              {/* HR — live, read-only view of the Quickair HR app's data (backend/hr_bridge.py). */}
              <Route path="hr" element={<HrModule />} />
              {/* HR → Logbook — Logbook's tasks, team board and admin pages on its
                  live data (backend/hr_logbook.py); everything but Time Tracking. */}
              <Route path="hr/logbook" element={<Navigate to="/hr/logbook/mine" replace />} />
              <Route path="hr/logbook/:tab" element={<Logbook />} />
              {/* Sales Report module — new, additive routes only. Sidebar entry
                  lands in a later phase; reachable directly by URL until then. */}
              <Route path="sales-report" element={<SalesReporterApp />} />
              <Route path="sales-report/visits" element={<SalesVisits />} />
              <Route path="sales-report/expenses" element={<SalesExpenses />} />
              <Route path="sales-report/plans" element={<SalesPlans />} />
              <Route path="sales-report/tasks" element={<SalesTasks />} />
              <Route path="sales-report/reports" element={<SalesReports />} />
              <Route path="sales-report/home" element={<SalesHome />} />
              <Route path="sales-report/profile" element={<SalesProfile />} />
              {/* Design & Engineering module — new, additive routes
                  (backend/design_engineering/, mounted at /api/design). */}
              <Route path="design" element={<DesignDashboard />} />
              <Route path="design/approvals" element={<DesignApprovals />} />
              <Route path="design/requests" element={<DesignRequests />} />
              <Route path="design/items" element={<DesignItems />} />
              <Route path="design/documents" element={<DesignDocuments kind="documents" />} />
              <Route path="design/boms" element={<DesignBoms />} />
              <Route path="design/routings" element={<DesignRoutings />} />
              <Route path="design/changes" element={<DesignChanges />} />
              <Route path="design/standards" element={<DesignDocuments key="standards" kind="standards" />} />
              <Route path="design/npd" element={<DesignNpd />} />
              <Route path="design/reports" element={<DesignReports />} />
              <Route path="design/settings" element={<DesignSettings />} />
            </Route>
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
